/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';

import { MenuItem } from '@mui/material';
import { TIME_FORMAT, TIMES } from '../../../../constants';
import { isTimeDisabled } from '../../../../utils/isTimeDisabled';
import { useTelegram } from '../../../../hooks/useTelegram';
import { StyledTextField } from '../../../common/StyledTextField/StyledTextField';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { MeetingRoomUser } from '../../../../store/reducers/meetingRooms';
import styles from './styles';

const isBetween = require('dayjs/plugin/isBetween');

dayjs.extend(isBetween);

interface StartTimePickerProps {
    setStartTimeValue: React.Dispatch<React.SetStateAction<Dayjs | null | undefined>>;
    startTimeValue: Dayjs | null | undefined;
    endTimeValue: Dayjs | null | undefined;
    chosenDateState?: string;
    setError: React.Dispatch<React.SetStateAction<string>>;
    startTimeError: string;
    editModeActive: boolean;
    checked: boolean;
    setChecked: React.Dispatch<React.SetStateAction<boolean>>;
    meetingRoomUsers: MeetingRoomUser[];
    disabled?: boolean;
    setIsStartTimeChanged: (isChanged: boolean) => void;
    isAdminRoute?: boolean;
    chosenUserTelegramId?: string;
}

function StartTimePicker({
    startTimeValue,
    setStartTimeValue,
    chosenDateState,
    endTimeValue,
    setError,
    startTimeError,
    editModeActive,
    checked,
    setChecked,
    meetingRoomUsers,
    disabled,
    setIsStartTimeChanged,
    isAdminRoute,
    chosenUserTelegramId,
}: StartTimePickerProps) {
    const { colorScheme, tgUserId } = useTelegram();

    const editedMeetingRoomBooking = useAppSelector(
        (store) => store.meetingRoomsReducer.editedUserMeetingRoom,
    );

    const todayDate = dayjs().format('YYYY-MM-DD');

    const allMeetingRoomUsers = useAppSelector(
        (store) => store.meetingRoomsReducer.allMeetingRoomUsers,
    ).filter((m) => {
        if (isAdminRoute) {
            if (editedMeetingRoomBooking) {
                return (
                    String(m.telegramUserId) === chosenUserTelegramId &&
                    m.userMeetingRoomInfo.id !== editedMeetingRoomBooking?.userMeetingRoomInfo.id
                );
            }
            if (chosenUserTelegramId) {
                return String(m.telegramUserId) === chosenUserTelegramId;
            }
        }
        return (
            Number(m.telegramUserId) === tgUserId &&
            m.userMeetingRoomInfo.id !== editedMeetingRoomBooking?.userMeetingRoomInfo.id
        );
    });

    useEffect(() => {
        if (startTimeError) {
            setStartTimeValue(null);
        }
    }, [startTimeError]);

    useEffect(() => {
        if (isAdminRoute && chosenUserTelegramId) {
            const isTimeShouldBeReset = allMeetingRoomUsers.some((booking) => {
                const formattedStartTime = dayjs(
                    booking.userMeetingRoomInfo.startTime,
                    TIME_FORMAT,
                ).format(TIME_FORMAT);
                return (
                    (String(booking.telegramUserId) === chosenUserTelegramId &&
                        formattedStartTime === startTimeValue?.format(TIME_FORMAT)) ||
                    // @ts-ignore
                    startTimeValue?.isBetween(
                        dayjs(`${todayDate} ${booking.userMeetingRoomInfo.startTime}`),
                        dayjs(`${todayDate} ${booking.userMeetingRoomInfo.endTime}`),
                        '[]',
                    )
                );
            });

            if (isTimeShouldBeReset) {
                setStartTimeValue(null);
            }
        }
    }, [
        startTimeValue,
        endTimeValue,
        chosenUserTelegramId,
        isAdminRoute,
        allMeetingRoomUsers,
        meetingRoomUsers,
    ]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value) {
            const nowDay = dayjs().format('YYYY-MM-DD');

            const newStartTime = dayjs(`${nowDay} ${e.target.value}:00`);

            setStartTimeValue(newStartTime);
            setChecked(false);
            setIsStartTimeChanged(true);
        }
    };

    return (
        <div
            css={styles.timePickerWrapperStyles({
                colorScheme,
                isEdited: !!disabled,
            })}
        >
            <StyledTextField
                colorScheme={colorScheme}
                select
                label="Start Time"
                disabled={disabled}
                value={startTimeValue?.format(TIME_FORMAT)}
                variant="outlined"
                onChange={handleChange}
                SelectProps={{
                    MenuProps: {
                        autoFocus: false,
                        PaperProps: styles.menuPaperStyles({ colorScheme }),
                    },
                }}
                InputLabelProps={{
                    className: 'inputLabel',
                    shrink: true,
                }}
            >
                {TIMES.map((t) => {
                    const isDisabled = isTimeDisabled(t, meetingRoomUsers);
                    const isDisabledInAllMeetingRoomUsers = isTimeDisabled(t, allMeetingRoomUsers);

                    return (
                        <MenuItem
                            autoFocus={t === startTimeValue?.format(TIME_FORMAT)}
                            value={t}
                            disabled={isDisabled || isDisabledInAllMeetingRoomUsers}
                            key={t}
                            css={styles.menuItemStyles({ colorScheme })}
                        >
                            {t}
                        </MenuItem>
                    );
                })}
            </StyledTextField>
        </div>
    );
}

export default StartTimePicker;
